import { craftingRecipes, craftingQueueItemKeys } from '../config/craftingRecipesConfig.js';

/**
 * A centralized utility function to build a complete, hydrated game configuration object.
 * This is the single source of truth for how a game config is constructed from keys.
 * @param {object} params - The parameters for building the config.
 * @param {object} params.masterItemsConfig - The master list of all item definitions.
 * @param {object} params.baseConfig - The base config with non-biome-specific keys.
 * @param {object} params.biomesConfig - The configuration for all biomes.
 * @param {string} params.biomeKey - The key for the selected biome.
 * @param {string} params.pathLength - The key for the selected path length.
 * @param {number} params.gridSize - The size of the game grid.
 * @returns {object|null} A fully hydrated game configuration object.
 */
export function buildGameConfig({ masterItemsConfig, baseConfig, biomesConfig, biomeKey, pathLength, gridSize, isAnimalsEnabled, gameMode, isDisastersEnabled, isRandomQueueEnabled, isNpcsEnabled, isStationaryObjectsEnabled, isPowerupsEnabled, isSettlersModeEnabled, demoNpcSpawns, isMiniMerge }) {
    // Retrieve the specific biome configuration using the provided biomeKey.
    const themeConfig = biomesConfig[biomeKey];
    // If the biome configuration is not found, return null as the config cannot be built.
    if (!themeConfig) return null;

    // Find the specific upgrade path data within the theme configuration.
    const pathData = themeConfig.upgradePathLinear.find(p => p.key === pathLength);
    // Destructure themeConfig to separate upgradePathLinear from the rest of the properties.
    // This ensures immutability and prevents circular references if upgradePathLinear were deeply nested.
    const { upgradePathLinear, ...restOfThemeConfig } = themeConfig;
    
    // Flatten the masterItemsConfig for easy lookups.
    const flatMasterItems = Object.values(masterItemsConfig).reduce((acc, category) => {
        return { ...acc, ...category };
    }, {});
    
    // Get the array of item keys for the selected upgrade path, defaulting to an empty array if not found.
    const upgradePathKeys = pathData?.path || [];
    // Map the upgrade path keys to their full item objects using the masterItemsConfig.
    let upgradePath = upgradePathKeys.map(key => flatMasterItems[key]);

    if (gameMode === 'crafting') {
        upgradePath = craftingQueueItemKeys.map(key => flatMasterItems[key]);
    }

    // Hydrate various item lists (animals, stationary objects, NPCs)
    // by mapping their keys from baseConfig to their full definitions in masterItemsConfig.
    const animals = isAnimalsEnabled ? (masterItemsConfig.animals ? Object.values(masterItemsConfig.animals) : []) : [];
    const genericUpgradePath = Object.values(masterItemsConfig.special || {});
    const stationaryObjects = Object.values(masterItemsConfig.settlements || {});
    const npcCharacters = Object.values(masterItemsConfig.npcs || {});
    const nonHumanNpcCharacters = Object.values(masterItemsConfig.nonHumanNpcs || {});
    
    // Calculate score components based on grid size and path length.
    const gridSizeScore = gridSize * gridSize * 15;
    const pathLengthScore = upgradePath.length * 500;
    // Calculate the total target score, rounded to the nearest hundred.
    const targetScore = Math.round((gridSizeScore + pathLengthScore) / 100) * 100;

    // Assemble the final hydrated game configuration object.
    const finalConfig = {
        ...restOfThemeConfig, // Include all properties from the theme config except upgradePathLinear
        biomeKey,
        animals,
        genericUpgradePath,
        stationaryObjects,
        npcCharacters,
        nonHumanNpcCharacters,
        upgradePath,
        gridSize,
        pathLength,
        pathLengthName: pathData?.name || '', // Get the display name for the path length
        targetScore,
        isAnimalsEnabled: isAnimalsEnabled ?? true,
        gameMode: gameMode || 'linear', // Default to linear
        scoring: baseConfig.scoring,
        mechanics: baseConfig.mechanics,
        isDisastersEnabled: isDisastersEnabled ?? true,
        isRandomQueueEnabled: isRandomQueueEnabled ?? true,
        isNpcsEnabled: isNpcsEnabled ?? true,
        isStationaryObjectsEnabled: isStationaryObjectsEnabled ?? true,
        isPowerupsEnabled: isPowerupsEnabled ?? true,
        demoNpcSpawns: demoNpcSpawns || [],
        isSettlersModeEnabled: isSettlersModeEnabled ?? false,
        isMiniMerge: isMiniMerge ?? false
        
    };

    if (finalConfig.gameMode === 'crafting') {
        finalConfig.recipes = craftingRecipes;
        finalConfig.pathLengthName = 'Crafting'; // Override path length name for UI
    }

    return finalConfig;
}

/**
 * Creates a throttled function that only invokes `func` at most once per `delay` milliseconds.
 * Useful for limiting the rate at which a function is called, e.g., on resize or scroll events.
 * @param {Function} func The function to throttle.
 * @param {number} [delay=500] The number of milliseconds to throttle invocations to. Defaults to 500ms.
 * @returns {Function} Returns the new throttled function.
 */
export function throttle(func, delay = 500) {
    // Flag to track if a function call is currently in progress within the delay period.
    let inProgress = false;
    return function(...args) {
        // If a call is already in progress, ignore subsequent calls until the delay passes.
        if (inProgress) return;
        // Set the flag to true to indicate a call is now in progress.
        inProgress = true;
        // After the specified delay, reset the flag to allow future calls.
        setTimeout(() => {
            inProgress = false;
        }, delay);
        // Execute the original function with its context and arguments.
        func.apply(this, args);
    };
}

/**
 * Loads an HTML component from a file and injects it into a target element.
 * This is useful for dynamically loading parts of the UI.
 * @param {string} componentName - The name of the component file (e.g., 'nav').
 * @param {string} targetSelector - The CSS selector of the element to inject into.
 * @returns {Promise<void>} A promise that resolves when the component is loaded.
 */
export async function loadComponent(componentName, targetSelector) {
    // Find the target HTML element where the component will be injected.
    const targetElement = document.querySelector(targetSelector);
    // If the target element is not found, log an error and exit.
    if (!targetElement) {
        console.error(`Target element "${targetSelector}" not found.`);
        return;
    }

    try {
        // Construct the path to the component's HTML file.
        // Assumes components are located in 'components/' relative to the project root.
        const response = await fetch(`components/${componentName}.html`);
        // Check if the network request was successful.
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        // Get the HTML content from the response and inject it into the target element.
        targetElement.innerHTML = await response.text();
    } catch (error) {
        // If an error occurs during fetching or injection, log it and display a user-friendly message.
        console.error(`Failed to load component "${componentName}":`, error);
        targetElement.innerHTML = '<p class="text-red-500 text-center">Error loading component.</p>';
    }
}

/**
 * Calculates the weighted probabilities for a list of items based on a logarithmic curve.
 * This ensures earlier items are more common while later items still have a chance to appear.
 * @param {Array<any>} items - The array of items to calculate weights for.
 * @returns {{weights: number[], totalWeight: number}} An object containing the array of weights and their sum.
 */
export function calculateWeightedProbabilities(items) {
    // This base rate can be tweaked to adjust the overall curve steepness.
    // A higher value makes the first items more common.
    const BASE_FALLOFF = 1.9;

    // Dynamically adjust the falloff rate based on the number of items.
    // This ensures that for longer paths, the curve flattens slightly,
    // guaranteeing the last item always has a meaningful chance (e.g., >1%).
    // The logarithm helps the adjustment to be more pronounced for shorter lists
    // and less so for longer ones, preventing it from becoming too flat.
    const numItems = items.length;
    const FALLOFF_RATE = BASE_FALLOFF - (Math.log10(numItems) / 2);

    const weights = items.map((_, index) => {
        // The weight is inversely proportional to the item's position raised to the falloff rate.
        return 1 / Math.pow(index + 1, FALLOFF_RATE);
    });
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    return { weights, totalWeight };
}